import { useState, useCallback } from "react"
import { useDispatch } from "react-redux"
import { determineDeactivateMode } from "../../utils/determineDeactivateMode"
import {
  checkCrewDeactivation,
  checkCrewDeletion,
  deactivateCrewMember,
  deleteCrewMember,
  activateCrewMember,
  getCrewProfiles
} from "store/crew-profiles"
import { toaster } from "components/common/Toast"

/**
 * Custom hook for managing crew deactivation/deletion modal state
 * @returns {Object} Modal states and handlers
 */
export const useCrewDeactivateModal = () => {
  const dispatch = useDispatch()
  const [deactivateDeleteModalState, setDeactivateDeleteModalState] = useState({
    visible: false,
    action: null, // 'deactivate' or 'delete'
    crew: null,
    mode: null, // Mode determined from API response
    details: null // Detailed data from API
  })
  const [activateModalVisible, setActivateModalVisible] = useState(false)
  const [selectedCrew, setSelectedCrew] = useState(null)
  const [loading, setLoading] = useState(false)

  const closeDeactivateDeleteModal = useCallback(() => {
    setDeactivateDeleteModalState({
      visible: false,
      action: null,
      crew: null,
      mode: null,
      details: null
    })
    setActivateModalVisible(false)
    setSelectedCrew(null)
  }, [])

  // Fetch deactivation/deletion details from API
  const fetchDeactivationDetails = useCallback(async (crewId, action) => {
    setLoading(true)
    try {
      // Call appropriate API endpoint based on action
      const response = action === "deactivate"
        ? await dispatch(checkCrewDeactivation(crewId))
        : await dispatch(checkCrewDeletion(crewId))

      const mode = determineDeactivateMode(response)
      setDeactivateDeleteModalState((prev) => ({
        ...prev,
        details: response,
        mode
      }))
    } catch (error) {
      console.error(`Failed to fetch details:`, error)
      closeDeactivateDeleteModal()
    } finally {
      setLoading(false)
    }
  }, [dispatch, closeDeactivateDeleteModal])

  const openDeactivateModal = useCallback((crew) => {
    setDeactivateDeleteModalState({
      visible: true,
      action: "deactivate",
      crew,
      mode: null,
      details: null
    })
    // Fetch deactivation details
    fetchDeactivationDetails(crew.id, "deactivate")
  }, [fetchDeactivationDetails])

  const openDeleteModal = useCallback((crew) => {
    setDeactivateDeleteModalState({
      visible: true,
      action: "delete",
      crew,
      mode: null,
      details: null
    })
    // Fetch deletion details
    fetchDeactivationDetails(crew.id, "delete")
  }, [fetchDeactivationDetails])

  const setDeactivateDeleteDetails = useCallback((details) => {
    const mode = determineDeactivateMode(details)
    setDeactivateDeleteModalState((prev) => ({
      ...prev,
      details,
      mode
    }))
  }, [])

  const handleConfirmDeactivateDelete = useCallback(async (data) => {
    setLoading(true)
    try {
      const { action, crew } = deactivateDeleteModalState

      // Call appropriate API endpoint based on action
      let response
      if (action === "deactivate") {
        response = await dispatch(deactivateCrewMember(crew.id, data))
      } else {
        response = await dispatch(deleteCrewMember(crew.id, data))
      }

      // Close modal
      closeDeactivateDeleteModal()

      // Show success message
      const successMessage = response?.message || `Crew member has been ${action}d successfully`
      toaster.success(successMessage)

      // Refresh crew profiles list to reflect the changes
      dispatch(getCrewProfiles())

      // Return success for the caller to handle
      return { success: true, action, response }
    } catch (error) {
      console.error(`Failed crew:`, error)
      return { success: false, error }
    } finally {
      setLoading(false)
    }
  }, [dispatch, deactivateDeleteModalState, closeDeactivateDeleteModal])

  const openActivateModal = useCallback((crew) => {
    setSelectedCrew(crew)
    setActivateModalVisible(true)
  }, [])

  const handleActivateConfirm = useCallback(async () => {
    setLoading(true)
    try {
      const response = await dispatch(activateCrewMember(selectedCrew.id))

      // Close modal
      setActivateModalVisible(false)
      setSelectedCrew(null)

      // Show success message
      const successMessage = response?.message || "Crew member has been activated successfully"
      toaster.success(successMessage)

      // Refresh crew profiles list to reflect the changes
      dispatch(getCrewProfiles())

      // Return success for the caller to handle
      return { success: true, response }
    } catch (error) {
      console.error("Failed to activate crew member:", error)
      return { success: false, error }
    } finally {
      setLoading(false)
    }
  }, [dispatch, selectedCrew])

  return {
    deactivateDeleteModalState,
    activateModalVisible,
    selectedCrew,
    openDeactivateModal,
    openDeleteModal,
    openActivateModal,
    closeDeactivateDeleteModal,
    setDeactivateDeleteDetails,
    handleConfirmDeactivateDelete,
    handleActivateConfirm,
    deactivateLoading: loading,
    deleteLoading: loading,
    deactivationDetailsLoading: loading,
    deletionDetailsLoading: loading
  }
}
